import React, { useState } from 'react';
import { Loader2, FileText, X, CheckCircle2, AlertTriangle, Copy, Send, Mail, Phone, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import DialogShell from '../kit/DialogShell';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { api, isSpendApprovalError } from '../api';
import { uc } from '../unifiedContactsApi';
import { toast } from '../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { errMsg } from './constants';
import { Field } from './fields';
import { formatDate } from '../kit/time';
import { copyText } from '../clipboard';
import { quietHoursDetail, confirmQuietHours } from '../quietHours';
import ContactSearchInput from '../shared/ContactSearchInput';
import useTextRecipient, { contactFields } from '../shared/useTextRecipient';

// ============================================================================
// SendToBorrowerModal — one-click SMS/email doc request with magic upload link
//
// Two ways in. From the income calculator it is a free-form request (`analysis`).
// From a loan file's Client Needs (`loan` + `requests`) the link is tied to that
// loan and to those rows: the recipient is prefilled from the loan, the list of
// documents is the rows' labels, and sending asks first.
// ============================================================================
const borrowerOf = (loan) => {
  const b = loan?.urla?.borrower || {};
  return {
    name: [b.first_name, b.last_name].filter(Boolean).join(' '),
    email: b.email || '',
    phone: b.cell_phone || '',
  };
};

export function SendToBorrowerModal({ analysis, onClose, loan, requests, onSent }) {
  const forLoan = !!loan && Array.isArray(requests);
  const prefill = forLoan ? borrowerOf(loan) : null;
  const [name, setName] = useState(prefill?.name || analysis?.borrower_name || '');
  const [email, setEmail] = useState(prefill?.email || '');
  const [phone, setPhone] = useState(prefill?.phone || '');
  const [picked, setPicked] = useState(null); // contact last picked from the name search (ticket 174)
  const { line: recipient, blocked } = useTextRecipient({ phone, email, name, picked });
  const [reason, setReason] = useState(
    forLoan
      ? 'To keep your loan moving forward we need a few documents from you.'
      : analysis?.variable_income?.checklist_message ||
        'To complete underwriting we need a few income documents from you.'
  );
  const [docs, setDocs] = useState(
    forLoan ? requests.map((r) => r.label) : analysis?.variable_income?.docs_needed || []
  );
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [leadName, setLeadName] = useState('');
  const [savingLeadName, setSavingLeadName] = useState(false);

  // Ticket 174: a pick fills all three fields (still editable); "+ New contact"
  // keeps the typed name and empties phone/email for typing.
  const pickContact = (c) => {
    const f = contactFields(c);
    setPicked(c);
    setName(f.name);
    setEmail(f.email);
    setPhone(f.phone);
  };
  const newPerson = (text) => {
    setPicked(null);
    setName(text);
    setEmail('');
    setPhone('');
  };

  const removeDoc = (i) => setDocs(docs.filter((_, j) => j !== i));

  const renameLead = async () => {
    if (!result?.created_lead || !leadName.trim()) return;
    setSavingLeadName(true);
    try {
      const [first_name, ...rest] = leadName.trim().split(' ');
      const updated = await uc.update(result.created_lead.contact_id, {
        first_name, last_name: rest.join(' ') || null,
      });
      setResult({ ...result, created_lead: { ...result.created_lead, display_name: updated.display_name } });
      toast({ title: 'Lead renamed' });
    } catch (e) {
      toast({ title: 'Failed to rename lead', description: errMsg(e, String(e)) });
    }
    setSavingLeadName(false);
  };

  const send = async () => {
    if (!email && !phone) { toast({ title: 'Enter an email or phone number' }); return; }
    if (forLoan) {
      const to = [email, phone].filter(Boolean).join(' and ');
      const ok = await confirmDialog({
        title: 'Send this upload link to the borrower?',
        description: `It goes to ${to}.`,
        confirmText: 'Send',
      });
      if (!ok) return;
    }
    setSending(true);
    try {
      const body = {
        borrower_name: name || 'Borrower',
        borrower_email: email || null,
        borrower_phone: phone || null,
        doc_types_needed: docs,
        reason,
        baseline_qualifying_monthly: analysis?.primary_calculation?.qualifying_monthly ?? null,
        baseline_base_ytd: analysis?.aggregate_pay?.base_ytd ?? null,
        baseline_variable_ytd: analysis?.aggregate_pay?.variable_ytd ?? null,
        ...(forLoan ? { loan_file_id: loan.loan_file_id, doc_request_ids: requests.map((r) => r.doc_request_id) } : {}),
      };
      let data;
      try {
        ({ data } = await api.post('/income/borrower-upload-request', body));
      } catch (e) {
        // Ticket 171: outside the send window the server refuses until the LO confirms.
        const qh = quietHoursDetail(e);
        if (!qh) throw e;
        if (!(await confirmQuietHours(qh))) { setSending(false); return; }
        ({ data } = await api.post('/income/borrower-upload-request', { ...body, confirm_quiet_hours: true }));
      }
      setResult(data);
      onSent?.(data);
      if (data.created_lead) setLeadName(data.created_lead.display_name || '');
      const channels = Object.entries(data.delivered).filter(([, v]) => v).map(([k]) => k);
      toast({
        title: channels.length ? `Sent via ${channels.join(' + ')}` : 'Upload link generated',
        description: channels.length ? 'Borrower will receive the link shortly' : 'SMS/email keys not configured — copy the message below and send manually.',
      });
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'Failed', description: errMsg(e, String(e)) });
    }
    setSending(false);
  };

  const copy = async (text, label) => {
    if (await copyText(text)) toast({ title: `Copied ${label}` });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  return (
    <DialogShell
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Send to Borrower"
      description={forLoan
        ? 'A secure upload link for these documents is created and delivered by email and text (if configured).'
        : 'One-click doc request: a secure magic upload link is created and delivered via email + SMS (if configured).'}
      className="max-w-xl max-h-[92vh] overflow-y-auto"
      data-testid="send-to-borrower-modal"
    >
        <div>
          {!result ? (
            <>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Borrower Name</Label>
                    <ContactSearchInput
                      value={name} onChange={setName} onPick={pickContact} onCreateNew={newPerson} testid="stb-name"
                      className="flex w-full rounded-md border border-input bg-transparent px-3 py-1 shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm mt-1 text-[13px] h-9"
                    />
                  </div>
                  <Field kind="phone" label="Phone (for SMS)" value={phone} onChange={setPhone} testId="stb-phone" />
                </div>
                <Field label="Email" value={email} onChange={setEmail} testId="stb-email" />
                <div>
                  <Label className="text-xs">Message</Label>
                  <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" data-testid="stb-reason" />
                </div>
                <div>
                  <Label className="text-xs flex items-center justify-between">
                    <span>Documents Requested ({docs.length})</span>
                    {!forLoan && <button onClick={() => setDocs([...docs, ''])} className="text-[11px] font-semibold text-brand-navy">+ Add</button>}
                  </Label>
                  <div className="mt-1 space-y-1.5">
                    {docs.map((d, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <FileText size={12} className="shrink-0 text-brand-accent" />
                        {forLoan
                          ? <span className="flex-1 text-[13px]" data-testid="stb-doc">{d}</span>
                          : <Input value={d} onChange={(e) => { const c = [...docs]; c[i] = e.target.value; setDocs(c); }} className="flex-1 text-[13px] h-8" />}
                        {!forLoan && <button onClick={() => removeDoc(i)} className="text-slate-400 hover:text-red-500"><X size={12} /></button>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {recipient && (
                <div className="mt-3 text-[12px] text-slate-600" data-testid="stb-recipient">{recipient}</div>
              )}
              <Button
                onClick={send}
                disabled={sending || blocked || (!email && !phone)}
                data-testid="stb-send"
                variant="navy"
                className="mt-5 w-full"
              >
                {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Generate & Send Link</>}
              </Button>
              <div className="mt-3 text-[10px] text-slate-400 text-center leading-relaxed">
                A link tied to a loan file stays valid while the loan is open; otherwise it expires in 14 days. Files uploaded by the borrower go straight into the loan file's Documents.
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="p-3 rounded-lg text-[12px] flex items-start gap-2" style={{ background: 'rgba(107,168,46,0.08)', color: '#3a5717' }}>
                <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
                <div>
                  <b>Magic upload link ready.</b>
                  {result.expires_at
                    ? ` Expires ${formatDate(result.expires_at)}.`
                    : ' Valid while the loan is open.'}
                  {result.delivered.email && ' Email sent.'}
                  {result.delivered.sms && ' SMS sent.'}
                  {!result.delivered.email && !result.delivered.sms && !result.sms_blocked && !result.sms_notice && ' Nothing was sent — copy the message below and send it manually.'}
                </div>
              </div>

              {result.created_lead && (
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200" data-testid="stb-created-lead">
                  <Label className="text-xs">
                    {result.created_lead.created ? 'Added to pipeline as' : 'Matched existing lead'}
                  </Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      className="flex-1 text-[13px] h-8"
                      data-testid="stb-lead-name"
                    />
                    <Button
                      onClick={renameLead}
                      disabled={savingLeadName || !leadName.trim() || leadName === result.created_lead.display_name}
                      variant="outline-navy"
                      className="h-8 px-3 text-[11px]"
                      data-testid="stb-lead-rename"
                    >
                      {savingLeadName ? <Loader2 size={12} className="animate-spin" /> : 'Rename'}
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs flex items-center justify-between">
                  <span className="flex items-center gap-1"><Link2 size={11} /> Upload Link</span>
                  <button onClick={() => copy(result.upload_url, 'link')} className="text-[11px] font-semibold flex items-center gap-1 text-brand-navy"><Copy size={10} /> Copy</button>
                </Label>
                <div className="mt-1 p-2.5 rounded-lg bg-slate-50 border border-slate-200 font-mono text-[11px] break-all" data-testid="stb-link">{result.upload_url}</div>
              </div>

              {result.email_body && (
                <div>
                  <Label className="text-xs flex items-center justify-between">
                    <span className="flex items-center gap-1"><Mail size={11} /> Email Body {result.delivered.email && <span className="text-[9px] font-bold uppercase text-emerald-600 ml-1">Sent</span>}</span>
                    <button onClick={() => copy(result.email_body, 'email')} className="text-[11px] font-semibold flex items-center gap-1 text-brand-navy"><Copy size={10} /> Copy</button>
                  </Label>
                  <div className="mt-1 p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-[11px] whitespace-pre-wrap max-h-40 overflow-y-auto">{result.email_body}</div>
                </div>
              )}

              {result.email_notice && !result.email_body && (
                <div className="rounded-lg p-3 bg-amber-50 border border-amber-200" data-testid="stb-email-notice">
                  <div className="text-[11px] uppercase font-semibold text-amber-800 mb-1 flex items-center gap-1"><AlertTriangle size={11} /> Email not sent</div>
                  <div className="text-xs text-amber-900">{result.email_notice}</div>
                </div>
              )}

              {result.sms_notice && (
                <div className="rounded-lg p-3 bg-amber-50 border border-amber-200" data-testid="stb-sms-notice">
                  <div className="text-[11px] uppercase font-semibold text-amber-800 mb-1 flex items-center gap-1"><AlertTriangle size={11} /> {['ghl_unconfirmed', 'ghl_unconfirmed_hold'].includes(result.sms_reason) ? 'Text not confirmed' : result.sms_reason === 'duplicate' ? 'Text already sent' : 'Text not sent'}</div>
                  <div className="text-xs text-amber-900">{result.sms_notice}</div>
                </div>
              )}

              {result.sms_body && (
                <div>
                  <Label className="text-xs flex items-center justify-between">
                    <span className="flex items-center gap-1"><Phone size={11} /> SMS Body {result.delivered.sms && <span className="text-[9px] font-bold uppercase text-emerald-600 ml-1">Sent</span>}</span>
                    <button onClick={() => copy(result.sms_body, 'SMS')} className="text-[11px] font-semibold flex items-center gap-1 text-brand-navy"><Copy size={10} /> Copy</button>
                  </Label>
                  <div className="mt-1 p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-[11px] whitespace-pre-wrap">{result.sms_body}</div>
                </div>
              )}

              <Button onClick={onClose} variant="outline-navy" className="w-full">
                Done
              </Button>
            </div>
          )}
        </div>
    </DialogShell>
  );
}
